import React from 'react';
import { createFilterOptions, styled } from '@mui/material';
import type { AutocompleteRenderGroupParams, FilterOptionsState } from '@mui/material';
import { AutoAwesome, History, HowToReg, Star } from '@mui/icons-material';
import { Colors } from 'library';
import { AutoComplete, Avatar, Chip, Row, Stack, Text } from 'library/ui';
import {
    PastWorkerDTO,
    PreferredWorkerDTO,
} from 'parent-portal/forms/components/pages/ProviderPreferencesPage/preferredWorkersApi';
import type { RequestedWorker } from '../formTypes';

const DISPLAY_LIMIT = 20;
const HINT_ID = -1;
const AVATAR_COLORS = [Colors.primary, Colors.secondary, Colors.accent, Colors.primaryMuted];

const GROUP_ICONS: Record<string, React.ReactNode> = {
    Preferred: <Star sx={{ fontSize: 14, color: Colors.textSecondary }} />,
    'Worked with before': <History sx={{ fontSize: 14, color: Colors.textSecondary }} />,
    'Teacher showcase': <AutoAwesome sx={{ fontSize: 14, color: Colors.textSecondary }} />,
    'Already requested': <HowToReg sx={{ fontSize: 14, color: Colors.textSecondary }} />,
};

interface WorkerOption extends RequestedWorker {
    group: string;
    subtitle?: string;
    disabled?: boolean;
    hint?: boolean;
}

export interface RequestedWorkerFieldsProps {
    pastWorkers: PastWorkerDTO[];
    preferredWorkers: PreferredWorkerDTO[];
    spotlightWorkers: RequestedWorker[];
    requestedWorkers: RequestedWorker[];
    onToggleWorker: (worker: RequestedWorker) => void;
    existingMatches?: RequestedWorker[];
}

const baseFilter = createFilterOptions<WorkerOption>();

function filterWithHint(options: WorkerOption[], state: FilterOptionsState<WorkerOption>): WorkerOption[] {
    const filtered = baseFilter(options, state);
    if (filtered.length <= DISPLAY_LIMIT) return filtered;

    const limited = filtered.slice(0, DISPLAY_LIMIT);
    return [
        ...limited,
        {
            id: HINT_ID,
            name: `Showing ${DISPLAY_LIMIT} of ${filtered.length} teachers — keep typing to narrow`,
            spotlight: false,
            group: '',
            hint: true,
        },
    ];
}

function statusSubtitle(status?: string) {
    if (!status || status === 'available') return undefined;
    return status.replaceAll('_', ' ').replace(/^./, (c) => c.toUpperCase());
}

function byName(a: WorkerOption, b: WorkerOption) {
    return a.name.localeCompare(b.name);
}

export function RequestedWorkerFields({
    pastWorkers,
    preferredWorkers,
    spotlightWorkers,
    requestedWorkers,
    onToggleWorker,
    existingMatches = [],
}: RequestedWorkerFieldsProps) {
    const matchedIds = new Set(existingMatches.map((worker) => worker.id));
    const preferredOptions: WorkerOption[] = preferredWorkers
        .filter((worker) => !matchedIds.has(worker.user.id))
        .map((worker) => ({
            id: worker.user.id,
            name: `${worker.user.first_name} ${worker.user.last_name}`,
            subtitle: statusSubtitle(worker.status) ?? worker.business_location?.name,
            disabled: worker.status === 'out_of_compliance',
            spotlight: false,
            group: 'Preferred',
        }))
        .sort(byName);
    const preferredIds = new Set(preferredOptions.map((option) => option.id));
    const pastOptions: WorkerOption[] = pastWorkers
        .filter((worker) => !preferredIds.has(worker.id) && !matchedIds.has(worker.id))
        .map((worker) => ({
            id: worker.id,
            name: `${worker.first_name} ${worker.last_name}`,
            subtitle: statusSubtitle(worker.status),
            disabled: worker.status === 'out_of_compliance',
            spotlight: false,
            group: 'Worked with before',
        }))
        .sort(byName);
    const spotlightOptions: WorkerOption[] = spotlightWorkers.map((worker) => ({
        ...worker,
        group: 'Teacher showcase',
    }));
    const matchedOptions: WorkerOption[] = existingMatches
        .map((worker) => ({ ...worker, subtitle: 'Already requested', disabled: true, group: 'Already requested' }))
        .sort(byName);

    const options = [...preferredOptions, ...pastOptions, ...spotlightOptions, ...matchedOptions];
    const selected: WorkerOption[] = [...requestedWorkers, ...existingMatches].map((worker) => ({
        ...worker,
        group: '',
    }));
    const hasAnyWorkers = options.length > 0;

    const groupCounts: Record<string, number> = {
        Preferred: preferredOptions.length,
        'Worked with before': pastOptions.length,
        'Teacher showcase': spotlightOptions.length,
        'Already requested': matchedOptions.length,
    };

    const renderGroup = (params: AutocompleteRenderGroupParams) => {
        if (params.group === '') {
            return <li key={params.key}>{params.children}</li>;
        }
        const total = groupCounts[params.group] ?? 0;
        const visible = React.Children.count(params.children);
        return (
            <li key={params.key}>
                <GroupHeader gap="sm">
                    {GROUP_ICONS[params.group]}
                    <Text size="xs" weight="semibold" tone="secondary">
                        {params.group.toUpperCase()} &middot; {visible < total ? `${visible} of ${total}` : total}
                    </Text>
                </GroupHeader>
                <GroupItems>{params.children}</GroupItems>
            </li>
        );
    };

    const matches = (a: RequestedWorker, b: RequestedWorker) => a.id === b.id && a.spotlight === b.spotlight;

    const handleSelect = (next: WorkerOption[]) => {
        const added = next.filter(
            (option) =>
                !option.hint &&
                !matchedIds.has(option.id) &&
                !requestedWorkers.some((worker) => matches(worker, option)),
        );
        const removed = requestedWorkers.filter((worker) => !next.some((option) => matches(worker, option)));
        [...added, ...removed].forEach((worker) =>
            onToggleWorker({
                id: worker.id,
                name: worker.name,
                spotlight: worker.spotlight,
                pictureUrl: worker.pictureUrl,
            }),
        );
    };

    if (!hasAnyWorkers) {
        return (
            <Text tone="secondary">
                Once you&apos;ve worked with Tandem teachers, you can request them here so they get first dibs.
            </Text>
        );
    }

    return (
        <>
            {(existingMatches.length > 0 || requestedWorkers.length > 0) && (
                <Row gap="sm" wrap>
                    {existingMatches.map((worker) => (
                        <Chip
                            key={`match-${worker.id}`}
                            bgColor={Colors.secondarySoft}
                            borderColor={Colors.secondary}
                            textColor={Colors.primary}
                        >
                            {worker.name}
                        </Chip>
                    ))}
                    {requestedWorkers.map((worker) => (
                        <Chip
                            key={`${worker.spotlight ? 'spotlight' : 'worker'}-${worker.id}`}
                            bgColor={Colors.secondarySoft}
                            borderColor={Colors.secondary}
                            textColor={Colors.primary}
                            removable
                            onRemove={() => onToggleWorker(worker)}
                        >
                            {worker.name}
                        </Chip>
                    ))}
                </Row>
            )}
            <AutoComplete
                multiple
                options={options}
                selected={selected}
                onSelect={handleSelect}
                placeholder="Add teachers you trust"
                getOptionLabel={(option) => option.name}
                getOptionSelected={(option, value) => matches(option, value)}
                getOptionDisabled={(option) => !!option.disabled || !!option.hint}
                groupBy={(option) => option.group}
                renderGroup={renderGroup}
                filterOptions={filterWithHint}
                disableCloseOnSelect
                renderTags={() => null}
                renderOption={(option) =>
                    option.hint ? (
                        <Text size="sm" tone="secondary">
                            {option.name}
                        </Text>
                    ) : (
                        <Row gap="sm">
                            <Avatar
                                name={option.name}
                                imageUrl={option.pictureUrl}
                                size={36}
                                color={AVATAR_COLORS[Math.abs(option.id) % AVATAR_COLORS.length]}
                            />
                            <Stack gap="none">
                                <Text weight="semibold">{option.name}</Text>
                                {option.subtitle && (
                                    <Text size="sm" tone="secondary">
                                        {option.subtitle}
                                    </Text>
                                )}
                            </Stack>
                        </Row>
                    )
                }
            />
        </>
    );
}

const GroupHeader = styled(Row)({
    padding: '10px 16px 4px',
});

const GroupItems = styled('ul')({
    padding: 0,
    margin: 0,
});
